import {
  Children,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { Link, type LinkProps } from "react-router";

import { useFloatingTree } from "@floating-ui/react";
import { CheckIcon, ChevronRightIcon } from "lucide-react";

import { cx, styled } from "simplydo/react/styled";
import { asIcon, Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { isElementOfType } from "simplydo/react/ui/utils";

import {
  dropdownItemCheck,
  dropdownItemLabel,
  dropdownItemLeading,
  dropdownItemSubmenuCaret,
  StyledDropdownItem,
  StyledDropdownItemDescription,
} from "./Dropdown.styles";
import type { DropdownItemImage, DropdownItemProps } from "./Dropdown.types";
import { useDropdownContext } from "./DropdownContext";

function resolveImage(image: DropdownItemImage | undefined): ReactNode {
  if (image == null || image === false) {
    return null;
  }
  if (typeof image === "string") {
    return <Image src={image} avatar />;
  }
  if (typeof image === "object" && "src" in image && !("$$typeof" in image)) {
    const { src, avatar, ...rest } = image as {
      src?: string;
      avatar?: boolean;
      [key: string]: unknown;
    };
    return <Image src={src} avatar={avatar ?? true} {...(rest as object)} />;
  }
  return image as ReactNode;
}

export type RenderDropdownItemProps = DropdownItemProps & {
  ref?: Ref<HTMLElement>;
  $dropdownTrigger?: boolean;
  $highlighted?: boolean;
  asLink?: boolean;
  to?: LinkProps["to"];
  href?: string;
  target?: string;
  rel?: string;
};

export const RenderDropdownItem = (
  props: RenderDropdownItemProps,
): ReactElement => {
  const {
    children,
    className,
    description,
    disabled,
    icon,
    image,
    label,
    onClick,
    value,
    style,
    onMouseEnter,
    onMouseLeave,
    onKeyDown,
    ref,
    $dropdownTrigger,
    $highlighted,
    asLink,
    to,
    href,
    target,
    rel,
    ...rest
  } = props;

  const ctx = useDropdownContext();
  const tree = useFloatingTree();
  const isLinkItem = Boolean(asLink);
  const isTrigger = Boolean($dropdownTrigger);

  const renderedIcon = asIcon(icon);
  const renderedImage = resolveImage(image);
  const [firstChild, ...restChildren] = Children.toArray(children);
  const leadingChild = isElementOfType(firstChild, [Icon, Image, "img"])
    ? firstChild
    : null;
  const hasLeading =
    renderedImage != null ||
    renderedIcon != null ||
    label != null ||
    leadingChild != null;
  const closeMenus = () => {
    if (ctx?.closeOnChange) {
      tree?.events.emit("click");
    }
  };
  const handleActivate = (event: MouseEvent<HTMLElement>) => {
    if (disabled || isTrigger) {
      if (disabled) {
        event.preventDefault();
      }
      return;
    }
    onClick?.(event, props);
    if (ctx && value !== undefined) {
      ctx.selectOption(event, value);
    } else if (ctx && !ctx.multiple && ctx.closeOnChange) {
      ctx.setOpen(false, event);
    }
    closeMenus();
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Always isolate from ancestors (e.g. table row click). Portaled menus
    // still bubble through the React tree even when detached in the DOM.
    event.stopPropagation();
    if (isTrigger) {
      onClick?.(event, props);
      return;
    }
    if (disabled) {
      event.preventDefault();
      return;
    }
    handleActivate(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || disabled || isTrigger) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      if (isLinkItem) {
        // Let the link's own click navigate; handleClick closes the menu.
        // Closing here would move focus to the trigger before the browser
        // activates the link, swallowing the navigation.
        if (event.key === " ") {
          event.preventDefault();
          event.currentTarget.click();
        }
        return;
      }
      event.preventDefault();
      handleActivate(event as unknown as MouseEvent<HTMLElement>);
    }
  };

  const isSelected =
    ctx && value !== undefined
      ? ctx.multiple
        ? Array.isArray(ctx.value) && ctx.value.includes(value)
        : ctx.value === value
      : false;

  const rootAs = asLink ? (href ? "a" : Link) : "div";
  const linkProps = asLink
    ? href
      ? { href, target, rel }
      : { to, target, rel }
    : {};

  return (
    <StyledDropdownItem
      ref={ref as never}
      as={rootAs}
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      className={cx(
        className,
        $highlighted && "is-highlighted",
        isSelected && "is-selected",
        disabled && "is-disabled",
      )}
      style={style}
      $highlighted={Boolean($highlighted)}
      $selectable={!asLink && ctx?.value !== undefined}
      $disabled={Boolean(disabled)}
      {...(rest as object)}
      {...linkProps}
      {...(ctx
        ? ctx.getItemProps({
            active: Boolean($highlighted),
            selected: Boolean(isSelected),
          })
        : { role: "option", "aria-selected": Boolean(isSelected) })}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isSelected ? (
        <Icon
          icon={CheckIcon}
          size="small"
          className={dropdownItemCheck}
          aria-hidden
        />
      ) : null}
      {hasLeading ? (
        <span className={dropdownItemLeading}>
          {renderedImage}
          {renderedIcon}
          {label}
          {leadingChild}
        </span>
      ) : null}
      <div className={dropdownItemLabel}>
        {leadingChild ? restChildren : children}
      </div>
      {description != null ? (
        <StyledDropdownItemDescription>
          {description}
        </StyledDropdownItemDescription>
      ) : null}
      {isTrigger ? (
        <span className={dropdownItemSubmenuCaret} aria-hidden>
          <Icon icon={ChevronRightIcon} />
        </span>
      ) : null}
    </StyledDropdownItem>
  );
};

type DropdownItemComponentProps = DropdownItemProps & {
  ref?: Ref<HTMLElement>;
  $dropdownTrigger?: boolean;
  $highlighted?: boolean;
};

const DropdownItemComponent = (
  props: DropdownItemComponentProps,
): ReactElement => <RenderDropdownItem {...props} />;

export const DropdownItem = styled(DropdownItemComponent)``;
